import { useCallback, useEffect, useMemo } from 'react';
import useRetrieveLatestAutomationVersion from '@/hooks/api/automationVersions/useRetrieveLatestAutomationVersion.ts';
import useUpdateAutomationVersion from '@/hooks/api/automationVersions/useUpdateAutomationVersion.ts';
import useRetrieveRunAutomationVersion from '@/hooks/api/runs/useRetrieveRunAutomationVersion.ts';
import { AutomationStep } from '@/types/automationSteps.ts';
import { isStepPinned as isStepPinnedInVersion, toggleStepPinnedOutputs } from '@/utils/pinnedOutputs.ts';
import { isLocalSubflowRuntimeStep } from '@/utils/runStepPresentation.ts';

export function usePinnedStep(step?: AutomationStep, runId?: string) {
  const { data: latestVersion } = useRetrieveLatestAutomationVersion();
  const { mutate: updatePinnedOutputs, isSuccess } = useUpdateAutomationVersion();

  const { data: runAutomationVersion, refetch } = useRetrieveRunAutomationVersion(runId);

  useEffect(() => {
    if (isSuccess && runId) refetch();
  }, [isSuccess, runId, refetch]);

  const version = runAutomationVersion ?? latestVersion;
  const isRuntimeLocalSubflowStep = isLocalSubflowRuntimeStep(
    runAutomationVersion,
    step?.step_id,
  );

  const isStepPinned = useMemo(
    () => isStepPinnedInVersion(version, step),
    [version, step],
  );

  const togglePinnedStep = useCallback(() => {
    if (!version || !step) return;
    updatePinnedOutputs({
      versionId: version.id,
      pinned_outputs: toggleStepPinnedOutputs(version, step),
    });
  }, [version, step, updatePinnedOutputs]);

  return {
    isStepPinned,
    togglePinnedStep: isRuntimeLocalSubflowStep ? undefined : togglePinnedStep,
  };
}
